import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { SpinoffAuthService } from '@sbe-spinoff-client/spinoff-platform';
import type { MyScoreDto } from '../generated-api/sbe-conduct/models';
import { ConductScoresService } from '../../features/members/data-access/conduct-scores.service';

/** `GET /me/orgs/:orgId/scores`로 로그인한 사람의 학교 내 자리를 읽는다. */
@Injectable({ providedIn: 'root' })
export class ConductMySeatsService {
  private readonly scores = inject(ConductScoresService);
  private readonly auth = inject(SpinoffAuthService);

  readonly loadedOrgId = signal<string | null>(null);
  readonly seats = signal<MyScoreDto[]>([]);
  readonly seatsFailed = signal(false);

  // A seat with no class is the school's own (staff) seat — the same rule the API uses
  // to tell staff from students (`ConductFacultyAccessService.assertFacultyAndFetchMembers`).
  readonly isFaculty = computed(() =>
    this.seats().some(
      (seat) => seat.relation === 'self' && seat.teamId === null,
    ),
  );
  readonly studentSeats = computed(() =>
    this.seats().filter((seat) => seat.teamId !== null),
  );

  private currentOrgId: string | null = null;
  private latestLoad = 0;
  private inFlight: { orgId: string; result: Promise<boolean> } | null = null;
  // The person the seats belong to, once known.
  private userId: string | null = null;

  constructor() {
    // A 학교종이 「진입」 can replace the session in the same tab without signing out; the
    // auth service then empties the current user. Another person's seats must not stay.
    effect(() => {
      const user = this.auth.currentUser();

      untracked(() => this.followUser(user?.id));
    });
  }

  // The guard only needs the role, so a loaded answer is reused.
  ensure(orgId: string): Promise<boolean> {
    if (this.currentOrgId === orgId && this.loadedOrgId() === orgId) {
      return Promise.resolve(true);
    }

    return this.refresh(orgId);
  }

  // Reads again (scores change), joining a request already on its way for the same school.
  async refresh(orgId: string): Promise<boolean> {
    if (this.inFlight?.orgId === orgId) return this.inFlight.result;

    const call = ++this.latestLoad;

    this.currentOrgId = orgId;
    this.loadedOrgId.set(null);
    this.seats.set([]);
    this.seatsFailed.set(false);

    const result = this.load(orgId, call);
    const load = { orgId, result };

    this.inFlight = load;
    try {
      return await result;
    } finally {
      if (this.inFlight === load) this.inFlight = null;
    }
  }

  clear() {
    this.latestLoad++;
    this.currentOrgId = null;
    this.inFlight = null;
    this.loadedOrgId.set(null);
    this.seats.set([]);
    this.seatsFailed.set(false);
  }

  private followUser(id: string | undefined) {
    if (!id) {
      if (this.userId) this.clear();
      this.userId = null;
      return;
    }

    if (this.userId && this.userId !== id) this.clear();
    this.userId = id;
  }

  private async load(orgId: string, call: number): Promise<boolean> {
    try {
      const { items } = await this.scores.listMine(orgId);

      if (call !== this.latestLoad || this.currentOrgId !== orgId) return false;

      this.seats.set(items);
      this.loadedOrgId.set(orgId);
      return true;
    } catch (error) {
      if (call !== this.latestLoad || this.currentOrgId !== orgId) return false;

      console.error('[conduct] loading my seats failed', error);
      this.seatsFailed.set(true);
      return false;
    }
  }
}
